import { overlayCss } from './styles';

const MAX_LOG_ROWS = 10;

export interface DebugOverlay {
  setMetric(key: string, value: string | number): void;
  log(label: string, args?: unknown[]): void;
  show(): void;
  hide(): void;
  destroy(): void;
}

export interface DebugOptions {
  /** Force enable/disable. If omitted, checks for `?debug=1` in the current URL. */
  enabled?: boolean;
}

function isEnabledByUrl(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  } catch {
    return false;
  }
}

export function createDebugOverlay(opts: DebugOptions = {}): DebugOverlay {
  const enabled = opts.enabled ?? isEnabledByUrl();

  if (!enabled) {
    // Return a no-op overlay so callers never need to null-check.
    return {
      setMetric() {},
      log() {},
      show() {},
      hide() {},
      destroy() {},
    };
  }

  // Inject CSS once.
  if (!document.querySelector('style[data-fg-debug-style]')) {
    const style = document.createElement('style');
    style.setAttribute('data-fg-debug-style', '');
    style.textContent = overlayCss;
    document.head.appendChild(style);
  }

  const root = document.createElement('div');
  root.setAttribute('data-fg-debug', '');
  const metricsSection = document.createElement('section');
  const logSection = document.createElement('section');
  logSection.setAttribute('data-log', '');
  root.appendChild(metricsSection);
  root.appendChild(logSection);
  document.body.appendChild(root);

  const metricRows = new Map<string, HTMLElement>();
  const logRows: HTMLElement[] = [];

  return {
    setMetric(key, value) {
      let row = metricRows.get(key);
      if (!row) {
        row = document.createElement('div');
        row.setAttribute('data-metric', key);
        const keySpan = document.createElement('span');
        keySpan.textContent = key;
        const valueStrong = document.createElement('strong');
        row.appendChild(keySpan);
        row.appendChild(valueStrong);
        metricsSection.appendChild(row);
        metricRows.set(key, row);
      }
      const strong = row.querySelector('strong');
      if (strong) strong.textContent = String(value);
    },

    log(label, args) {
      const row = document.createElement('div');
      row.setAttribute('data-log-row', '');
      row.textContent = args ? `${label} ${JSON.stringify(args)}` : label;
      logSection.appendChild(row);
      logRows.push(row);
      while (logRows.length > MAX_LOG_ROWS) {
        const old = logRows.shift();
        old?.remove();
      }
    },

    show() {
      root.style.display = '';
    },
    hide() {
      root.style.display = 'none';
    },
    destroy() {
      root.remove();
    },
  };
}
